import { Route, Switch, Redirect, useLocation } from 'wouter-preact';
import { useEffect, useState } from 'preact/hooks';
import { Explore } from './pages/explore';
import { useAuth } from './lib/session';
import type { ComponentChildren, ComponentType } from 'preact';

/**
 * Router. Auth gate redirects to /sign-in when no session exists.
 * Routes per public_web_platform_proposal.md §Trash Trail.
 *
 * `/explore` is the PUBLIC consumption surface (no auth) — see
 * docs/ARCHITECTURE.md. Contribution surfaces (/submit, /annotate,
 * /contributions, /review) remain sign-in-gated. `/` lands on /explore so
 * consumption-first onramp is preserved.
 *
 * Code-splitting: /explore is the landing route and the only page in the
 * entry chunk (the 40 KB first-paint budget in scripts/
 * check-bundle-size.mjs applies to it). Every other page loads through
 * `lazyPage` → Vite emits a per-page chunk fetched on first navigation.
 */

/**
 * Dependency-free lazy route wrapper (avoids pulling preact/compat's
 * Suspense into the entry). Shows the same "Loading…" shell the auth
 * gate uses while the chunk streams in.
 */
function lazyPage<P extends object>(
  load: () => Promise<Record<string, unknown>>,
  exportName: string,
): ComponentType<P> {
  return function LazyPage(props: P) {
    const [Comp, setComp] = useState<ComponentType<P> | null>(null);
    const [err, setErr] = useState<Error | null>(null);
    useEffect(() => {
      let alive = true;
      load()
        .then((mod) => {
          if (alive) setComp(() => mod[exportName] as ComponentType<P>);
        })
        .catch((e: unknown) => {
          if (alive) setErr(e instanceof Error ? e : new Error(String(e)));
        });
      return () => {
        alive = false;
      };
    }, []);
    if (err) {
      return (
        <p role="alert">
          Couldn't load this page ({err.message}). Check your connection and
          reload.
        </p>
      );
    }
    if (!Comp) return <p aria-live="polite">Loading…</p>;
    return <Comp {...props} />;
  };
}

const SignIn = lazyPage(() => import('./pages/sign-in'), 'SignIn');
const AuthComplete = lazyPage(() => import('./pages/auth-complete'), 'AuthComplete');
const Submit = lazyPage(() => import('./pages/submit'), 'Submit');
const Contributions = lazyPage(() => import('./pages/contributions'), 'Contributions');
const Review = lazyPage(() => import('./pages/review'), 'Review');
const Label = lazyPage(() => import('./pages/label'), 'Label');
const Annotate = lazyPage<{ id: string }>(() => import('./pages/annotate'), 'Annotate');
const Orgs = lazyPage(() => import('./pages/orgs'), 'Orgs');
const Cleanups = lazyPage(() => import('./pages/cleanups'), 'Cleanups');
const Privacy = lazyPage(() => import('./pages/privacy'), 'Privacy');
const Terms = lazyPage(() => import('./pages/terms'), 'Terms');
const Report = lazyPage<{ id: string }>(() => import('./pages/report'), 'Report');
const Account = lazyPage(() => import('./pages/account'), 'Account');
const DevItemEditList = lazyPage(
  () => import('./pages/dev-item-edit-list'),
  'DevItemEditList',
);

export function App() {
  return (
    <Switch>
      <Route path="/explore" component={Explore} />
      <Route path="/sign-in">
        <SignIn />
      </Route>
      {/* Google OAuth landing (#token=…&next=… fragment) — must NOT sit
          behind RequireAuth: the session is being established here. It
          immediately location.replace()s away, so it's exempt from the
          BackLink rule in tests/components/navigation.test.tsx. */}
      <Route path="/auth/complete">
        <AuthComplete />
      </Route>
      <Route path="/submit">
        <RequireAuth><Submit /></RequireAuth>
      </Route>
      <Route path="/contributions">
        <RequireAuth><Contributions /></RequireAuth>
      </Route>
      <Route path="/review">
        <RequireAuth><Review /></RequireAuth>
      </Route>
      <Route path="/label">
        <RequireAuth><Label /></RequireAuth>
      </Route>
      <Route path="/annotate/:id">
        {(params) => (
          <RequireAuth><Annotate id={params.id} /></RequireAuth>
        )}
      </Route>
      {/* Org/crew accounts (issue #57) — reached from /contributions. */}
      <Route path="/orgs">
        <RequireAuth><Orgs /></RequireAuth>
      </Route>
      {/* Cleanup history — reached from the /account activity hub. */}
      <Route path="/cleanups">
        <RequireAuth><Cleanups /></RequireAuth>
      </Route>
      {/* Account settings — currently just deletion. Behind RequireAuth:
          there is no account to delete without a session. Required by App
          Store Guideline 5.1.1(v), and the privacy policy names this exact
          path ("Your contributions -> Account settings -> Delete account"),
          so the route and the policy have to ship together or the policy
          instructs App Review down a dead end. */}
      <Route path="/account">
        <RequireAuth><Account /></RequireAuth>
      </Route>
      {/* Privacy policy. PUBLIC on purpose: App Store Connect requires a
          reachable policy URL, and App Review loads it without signing in.
          A policy behind an auth wall is not a policy. */}
      <Route path="/privacy">
        <Privacy />
      </Route>
      {/* Terms of use. PUBLIC for the same reason as /privacy: App Review
          reads it signed out, and the sign-in screen's agreement line
          links here — terms you cannot read before agreeing are not
          terms. Guideline 1.2 wants the zero-tolerance clause on this
          page and the contact address it publishes. */}
      <Route path="/terms">
        <Terms />
      </Route>
      {/* Report a photo (Guideline 1.2b). The map popup can only emit a
          plain link — it is an HTML string with no route into module
          scope — so the flag control on a pin lands here. Gated: an
          anonymous report would be a button for pulling any pin off the
          map, because the first report quarantines. */}
      <Route path="/report/:id">
        {(params) => (
          <RequireAuth><Report id={params.id} /></RequireAuth>
        )}
      </Route>
      {/* Dev/demo route — see src/pages/dev-item-edit-list.tsx. Public
          (no auth) because it serves only fixture data, no user PII. Not
          linked from the main nav; reachable only by direct URL. */}
      <Route path="/dev/item-edit-list">
        <DevItemEditList />
      </Route>
      <Route>
        <Redirect to="/explore" />
      </Route>
    </Switch>
  );
}

function RequireAuth({ children }: { children: ComponentChildren }) {
  const { user, loading } = useAuth();
  const [location] = useLocation();
  if (loading) return <p aria-live="polite">Loading…</p>;
  if (!user) {
    const dest = `/sign-in?next=${encodeURIComponent(location)}`;
    return <Redirect to={dest} />;
  }
  return <>{children}</>;
}
